import { isProductionBuilding } from "./buildings";
import type { GameState, PlayerId, ProductionBuildingKind, UnitKind } from "./types";
import { isBuildingTile } from "./types";
import { movementType, type MovementType } from "./units";

export const CAPACITY_PER_PRODUCTION_BUILDING = 5;

const CAPACITY_CONTRIBUTION_BY_PRODUCTION_BUILDING: Record<ProductionBuildingKind, MovementType[]> =
  {
    barracks: ["foot"],
    factory: ["vehicle"],
    base: ["foot", "vehicle"],
    airport: ["air"],
  };

const MOVEMENT_TYPES: readonly MovementType[] = ["foot", "vehicle", "air"];

export type PlayerCapacityStats = Record<
  PlayerId,
  Record<MovementType, { current: number; cap: number }>
>;

export type ProductionBuildingCapacity = { current: number; cap: number };
export type BaseProductionCapacity = Record<"foot" | "vehicle", ProductionBuildingCapacity>;

function emptyCapacityCounts(): Record<MovementType, number> {
  return { foot: 0, vehicle: 0, air: 0 };
}

function capacityContributionForBuilding(
  buildingKind: ProductionBuildingKind,
): readonly MovementType[] {
  return CAPACITY_CONTRIBUTION_BY_PRODUCTION_BUILDING[buildingKind];
}

export function capacityLimitForPlayer(
  state: GameState,
  player: PlayerId,
): Record<MovementType, number> {
  const caps = emptyCapacityCounts();
  for (const tile of state.tiles) {
    if (!isBuildingTile(tile.kind) || tile.owner !== player) continue;
    if (!isProductionBuilding(tile.kind)) continue;
    for (const mtype of capacityContributionForBuilding(tile.kind)) {
      caps[mtype] += CAPACITY_PER_PRODUCTION_BUILDING;
    }
  }
  return caps;
}

export function capacityUsedForPlayer(
  state: GameState,
  player: PlayerId,
): Record<MovementType, number> {
  const counts = emptyCapacityCounts();
  for (const unit of state.units) {
    if (unit.owner !== player) continue;
    counts[movementType(unit.kind)] += 1;
    if (unit.cargo) {
      counts[movementType(unit.cargo.kind)] += 1;
    }
  }
  return counts;
}

function plannedCapacityForMovementType({
  plannedKinds,
  movementType: mtype,
}: {
  plannedKinds: readonly UnitKind[];
  movementType: MovementType;
}): number {
  return plannedKinds.filter((kind) => movementType(kind) === mtype).length;
}

export function canBuildUnitKind({
  state,
  player,
  unitKind,
  plannedKinds = [],
}: {
  state: GameState;
  player: PlayerId;
  unitKind: UnitKind;
  plannedKinds?: readonly UnitKind[];
}): boolean {
  const mtype = movementType(unitKind);
  const current = capacityUsedForPlayer(state, player)[mtype];
  const planned = plannedCapacityForMovementType({ plannedKinds, movementType: mtype });
  const cap = capacityLimitForPlayer(state, player)[mtype];
  return current + planned < cap;
}

export function movementTypeForProductionBuilding(
  buildingKind: ProductionBuildingKind,
): MovementType {
  const [primary] = capacityContributionForBuilding(buildingKind);
  return primary!;
}

export function capacityForProductionBuilding({
  state,
  player,
  buildingKind,
}: {
  state: GameState;
  player: PlayerId;
  buildingKind: ProductionBuildingKind;
}): ProductionBuildingCapacity | BaseProductionCapacity {
  if (buildingKind === "base") {
    return capacityForBaseProduction({ state, player });
  }
  const mtype = movementTypeForProductionBuilding(buildingKind);
  const current = capacityUsedForPlayer(state, player)[mtype];
  const cap = capacityLimitForPlayer(state, player)[mtype];
  return { current, cap };
}

function capacityForBaseProduction({
  state,
  player,
}: {
  state: GameState;
  player: PlayerId;
}): BaseProductionCapacity {
  const current = capacityUsedForPlayer(state, player);
  const cap = capacityLimitForPlayer(state, player);
  return {
    foot: { current: current.foot, cap: cap.foot },
    vehicle: { current: current.vehicle, cap: cap.vehicle },
  };
}

export function capacityStatsForState(state: GameState): PlayerCapacityStats {
  const stats: PlayerCapacityStats = {
    p1: {
      foot: { current: 0, cap: 0 },
      vehicle: { current: 0, cap: 0 },
      air: { current: 0, cap: 0 },
    },
    p2: {
      foot: { current: 0, cap: 0 },
      vehicle: { current: 0, cap: 0 },
      air: { current: 0, cap: 0 },
    },
  };

  for (const player of ["p1", "p2"] as const) {
    const caps = capacityLimitForPlayer(state, player);
    const current = capacityUsedForPlayer(state, player);
    for (const mtype of MOVEMENT_TYPES) {
      stats[player][mtype] = { current: current[mtype], cap: caps[mtype] };
    }
  }

  return stats;
}
